import {
  forwardRef,
  type ComponentType,
  type InputHTMLAttributes,
  type SVGProps,
} from "react";
import { LuSearch, LuX } from "react-icons/lu";
import { siteTexts } from "../i18n/siteTexts";
import "./SearchInput.css";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;
const SearchIcon = LuSearch as unknown as IconLike;
const ClearIcon = LuX as unknown as IconLike;

type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  className?: string;
  clearLabel?: string;
  iconClassName?: string;
  inputType?: "text" | "search";
  onClear?: () => void;
};

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className = "",
      clearLabel = siteTexts.common.searchInput.clear,
      iconClassName = "",
      inputType = "text",
      onClear,
      ...inputProps
    },
    ref,
  ) => {
    const hasValue = String(inputProps.value ?? "").length > 0;
    const canClear = Boolean(
      onClear && hasValue && !inputProps.disabled && !inputProps.readOnly,
    );

    return (
      <div className={`tnp-search-input${className ? ` ${className}` : ""}`}>
        <span
          className={`tnp-search-input__icon${
            iconClassName ? ` ${iconClassName}` : ""
          }`}
          aria-hidden="true"
        >
          <SearchIcon />
        </span>
        <input ref={ref} type={inputType} {...inputProps} />
        {canClear && (
          <button
            type="button"
            className="tnp-search-input__clear"
            aria-label={clearLabel}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClear?.();
            }}
          >
            <ClearIcon aria-hidden="true" />
          </button>
        )}
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
